/**
 * Which way he is going, kept across reloads.
 *
 * Two things used to decide this and both forgot themselves on refresh: the clock made the
 * opening guess, and standing in one of the houses overrode it. A reload in the middle of the
 * ride home therefore threw away the leg he was on and offered the school run back, which is
 * the one moment the app has no business having an opinion.
 *
 * So a live session's direction is restored as it was, and only a session left alone long
 * enough to be over falls back to the guess.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { directionFromClock, isStickySession, parseDirection, serialiseDirection, type Direction } from "../core/sticky";
import { lastSeenMs } from "./lastSeen";

const STORAGE_KEY = "gotime.direction";

function read(): { direction: Direction; chosen: boolean } {
	const nowMs = Date.now();
	if (isStickySession(lastSeenMs(), nowMs)) {
		try {
			const stored = parseDirection(localStorage.getItem(STORAGE_KEY));
			if (stored) {
				return stored;
			}
		} catch {
			// Private browsing; the clock is the only opinion left.
		}
	}
	return { direction: directionFromClock(nowMs), chosen: false };
}

export interface DirectionControls {
	/** He said so. Nothing else gets to argue for the rest of the session. */
	choose: (next: Direction) => void;
	/** The phone worked it out, which only counts while he has not said otherwise. */
	infer: (next: Direction) => void;
}

export function useDirection(): [Direction, DirectionControls] {
	const [state, setState] = useState(read);

	useEffect(() => {
		try {
			localStorage.setItem(STORAGE_KEY, serialiseDirection(state));
		} catch {
			// Private browsing; the direction simply will not survive a reload.
		}
	}, [state]);

	const choose = useCallback((next: Direction) => {
		setState({ direction: next, chosen: true });
	}, []);

	const infer = useCallback((next: Direction) => {
		setState((current) => (current.chosen || current.direction === next ? current : { direction: next, chosen: false }));
	}, []);

	const controls = useMemo(() => ({ choose, infer }), [choose, infer]);
	return [state.direction, controls];
}
